/* Qué celda del padrón se puede tocar con el lápiz prendido.
 *
 * ── POR QUÉ EXISTE ────────────────────────────────────────────────────────
 *
 * Lo pidió Igna mirando el padrón: *«no me gusta ese popup para rellenar»*.
 * Con el lápiz prendido había que abrir la ficha lateral para corregir un
 * dato que ya estaba a la vista en la tabla — y en un padrón de 658 tractores
 * recién cargados, donde lo que hay que hacer es completar huecos, eso son
 * tres clics por celda.
 *
 * Es el mismo patrón que ya usa Documentos, que es la pantalla donde Marcos
 * corrige todos los días. Traerlo acá deja de haber dos formas de editar en
 * el hub según en qué pantalla estés.
 *
 * ── ES UNA LISTA NEGRA Y NO UNA BLANCA ───────────────────────────────────
 *
 * Se dice qué NO se toca y el resto queda editable. Al revés, la columna que
 * se agregue mañana nacería de sólo lectura y el síntoma sería «no me deja
 * escribir ahí», sin nada que lo explique — la misma falla que este sistema
 * ya tuvo cuatro veces. Con la lista negra el error va al otro lado: algo
 * nuevo nace editable y se cierra cuando alguien lo mira.
 */

/* LA CLAVE NO SE TOCA, y es lo único que rompería de verdad. `updateRow`
 * busca la fila por esa columna: cambiarla desde la celda no renombraría el
 * interno, escribiría sobre una fila que no existe —o peor, sobre otra— y el
 * `.single()` volvería con un error que no nombra el campo. Se corrige desde
 * la ficha, que es donde el formulario sabe lo que está haciendo. */
export const NO_SE_TOCA = new Set([
  "id",
  // Derivadas: las escribe un recálculo o un job, y lo que se tipee ahí se
  // pisa solo. Un campo que se guarda, dice «listo» y amanece con otra cosa
  // es peor que uno que no se puede editar — es la trampa de
  // `requiere_peligrosas`.
  "activo",
  "estado_calculado",
  // Quién tocó la fila y cuándo. Lo sella `updateRow`; escribirlo a mano
  // convertiría la columna de auditoría en algo que se puede falsear.
  "usuario",
  "usuario_nombre",
  "ultimaModificacion",
  "actualizado_en",
  "creado_en",
]);

/* El estado tiene su propia pastilla y su propio vocabulario —cinco valores
 * con un CHECK detrás— así que un campo de texto libre ahí manda a la base
 * algo que va a rechazar, y el cartel no nombra el campo. Se declara desde la
 * ficha, que dibuja las opciones. */
export const ES_ESTADO = (key) =>
  key === "estado" || key === "estado_manual" || key === "estadoDeclarado";

/* ── Y UNA COLUMNA DERIVADA NO ES UNA COLUMNA ─────────────────────────────
 *
 * Apareció probando el padrón de tractores con las columnas de verdad: de las
 * siete celdas que ofrecían input, TRES no se podían guardar nunca.
 * `nombreEmpresa`, `nombreSatelital` y `nombreResponsable` no existen en la
 * tabla — las arma `tractores.service.js` al enriquecer la fila, resolviendo
 * un CUIT contra el catálogo de empresas. Medido contra la API:
 *
 *     PATCH /rest/v1/tractores  {"nombreEmpresa": …}  → 400
 *     PGRST204 · Could not find the 'nombreEmpresa' column of 'tractores'
 *
 * O sea una celda que invita a escribir y sólo puede contestar «no se pudo
 * guardar» — la llave que no abre nada, con la vuelta de que ésta además se
 * ofrece. Y no es cosmético: lo que se quiso corregir ahí (la empresa de un
 * tractor) se corrige escribiendo el CUIT en `empresa`, que es otra columna.
 *
 * SE MARCA EN EL CATÁLOGO DE COLUMNAS y no acá: `Columnas.js` es donde alguien
 * decide qué se muestra, y es el mismo archivo que se toca el día que se
 * agrega otra derivada. Escrita como una lista de nombres en este módulo, la
 * próxima nacería editable y el síntoma volvería a ser un input que rebota.
 *
 * LA LISTA NEGRA SIGUE SIENDO LA REGLA, y esto no la invierte: una columna
 * REAL a la que se le olvide nada nace editable —que es lo que se quiere— y a
 * una derivada sin marcar le pasa lo que pasaba hasta hoy, que es ruidoso y se
 * ve. El error sigue yendo para el lado barato.
 */
export function sePuedeTocar(col, claveId) {
  const key = typeof col === "string" ? col : col?.key;
  if (!key) return false;
  if (key === claveId) return false;
  if (NO_SE_TOCA.has(key)) return false;
  if (ES_ESTADO(key)) return false;
  if (typeof col === "object" && col?.derivada) return false;
  return true;
}

/* Lo que se manda a guardar, o `null` si no hay nada que guardar.
 *
 * ── NO SE ESCRIBE SI NO CAMBIÓ ────────────────────────────────────────────
 *
 * Entrar y salir de una celda sin tocarla es lo que más pasa cuando se
 * recorre una tabla con el lápiz prendido. Guardando igual, cada paseo dejaría
 * la fila firmada con otro nombre y otra fecha — y la columna «Cargó», que
 * existe para saber a quién preguntarle, terminaría diciendo el último que
 * pasó por al lado.
 *
 * El vacío se compara contra el vacío en todas sus formas: `null`, `undefined`
 * y `""` son lo mismo para una celda, y sin esto salir de un campo vacío
 * escribiría `""` sobre un `null` y contaría como un cambio.
 */
export function cambio(valorOriginal, valorNuevo) {
  const antes = valorOriginal === null || valorOriginal === undefined
    ? ""
    : String(valorOriginal);
  const ahora = valorNuevo === null || valorNuevo === undefined
    ? ""
    : String(valorNuevo);
  if (antes.trim() === ahora.trim()) return null;
  return ahora.trim();
}
